<template>
  <BittsLoading :is-loading="!ready">
    <BittsTablePro
      ref="tableRef"
      :columns="columns"
      :rows="nonAdminPanelAuthsWithRoles"
      :zebra="true"
      :hide-pagination="true"
      :row-height="50"
      :passed-context="gridContext"
    />
  </BittsLoading>
</template>

<script setup lang="ts">
import { BittsLoading, BittsTablePro } from '@crossbeam/bitts';

import { IRowNode } from '@ag-grid-community/core';
import { computed, onMounted, ref } from 'vue';

import UserSelectionTableCell, {
  UserSelectionTableCellContext,
  UserSelectionTableCellParams,
} from '@/components/billing/UserSelectionTableCell.vue';

import useAuthorizationsWithRoles from '@/composables/team/useAuthorizationsWithRoles';
import { ADMIN_PANEL } from '@/constants/team';
import { useRolesStore, useTeamStore } from '@/stores';

const teamStore = useTeamStore();
const rolesStore = useRolesStore();
const { authsWithRoles, teamReady, rolesReady, selectedUserIds } =
  useAuthorizationsWithRoles();
const nonAdminPanelAuthsWithRoles = computed(() => {
  return authsWithRoles.value.filter(
    (auth) => 'authorizer_type' in auth && auth.authorizer_type !== ADMIN_PANEL,
  );
});

const userIdsToRemove = computed(() => {
  return authsWithRoles.value
    .filter((auth) => auth.user?.id && !selectedUserIds.value.has(auth.user.id))
    .map((auth) => auth.user?.id);
});

const ready = computed(() => teamReady.value && rolesReady.value);

const tableRef = ref<InstanceType<typeof BittsTablePro>>();

onMounted(async () => {
  await Promise.all([teamStore.readySync, rolesStore.readySync]);
  selectedUserIds.value = new Set(
    authsWithRoles.value
      .map((auth) => auth.user?.id)
      .filter((id): id is number => !!id),
  );
});

function handleUserSelectionChanged(
  rowData: UserSelectionTableCellParams | undefined,
  isChecked: boolean,
) {
  const userId = rowData?.user?.id;
  if (!userId) return;
  if (isChecked) selectedUserIds.value.add(userId);
  else selectedUserIds.value.delete(userId);
}
const gridContext: UserSelectionTableCellContext = {
  onUserSelectionChanged: handleUserSelectionChanged,
  isUserSelected: (userId) => !!userId && selectedUserIds.value.has(userId),
};

const columns = computed(() => [
  {
    headerName: 'Name',
    suppressMovable: true,
    cellRenderer: UserSelectionTableCell,
    flex: 1,
    sortable: true,
    comparator: (
      _valueA: string,
      _valueB: string,
      nodeA: IRowNode<UserSelectionTableCellParams>,
      nodeB: IRowNode<UserSelectionTableCellParams>,
    ) => {
      const nameA = nodeA.data?.user?.first_name ?? '';
      const nameB = nodeB.data?.user?.first_name ?? '';
      return nameA.localeCompare(nameB);
    },
  },
  {
    headerName: 'Role',
    suppressMovable: true,
    field: 'roleName',
    sortable: true,
  },
  {
    headerName: 'Status',
    suppressMovable: true,
    cellRenderer: UserSelectionTableCell,
  },
]);

defineExpose({ userIdsToRemove, selectedUserIds, nonAdminPanelAuthsWithRoles });
</script>
